import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  Space,
  Modal,
} from 'antd';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';

interface CategoryModelProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void; // Callback after successful submit
  initialValues?: {
    category_id?: number;
    category_name?: string;
    category_short_code?: string;
  } | null;
  isEditMode?: boolean;
}

const CategoryModel: React.FC<CategoryModelProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
}) => {
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);

  // Determine if submit button should be disabled based on form validation
  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  };

  // Handle form submit
  const handleAddCategory = async (values: any) => {
    try {
      const dataVal = {
        category_name: values.category_name,
        category_short_code: values.category_short_code,
        ...(initialValues?.category_id && { category_id: initialValues.category_id }),
      };
      const url = isEditMode
        ? 'edit-category'
        : 'add-category';

      const method = isEditMode ? 'put' : 'post';

      const data = await apiRequest({
        method,
        url,
        data: dataVal,
      });

      onCreate(data); // Notify parent about success
      showSuccess(
        isEditMode ? 'Category updated successfully!' : 'Category created successfully!',
      );
      form.resetFields();

      if (isModalClose) {
        onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
      }
    } catch (error) {
      showError(`Failed to ${isEditMode ? 'update' : 'create'} category`);
    }
  };

  // When modal opens or mode changes, set/reset form fields accordingly
  useEffect(() => {
    if (visible && isEditMode && initialValues) {
      form.setFieldsValue({
        category_name: initialValues.category_name,
        category_short_code: initialValues.category_short_code || undefined,
      });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, isEditMode, initialValues, form]);

  return (
    <Modal
      title={isEditMode ? 'Edit Category' : 'Add New Category'}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width="90%"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleAddCategory}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="category_name"
              label={
                <span>
                  Category Name&nbsp;
                  <span style={{ fontSize: '12px', color: '#888' }}>
                    (Max 255 characters)
                  </span>
                </span>
              }
              rules={[
                { required: true, message: 'Please enter the Category Name' },
                { max: 255, message: 'Maximum 255 characters allowed' },
              ]}
            >
              <Input
                showCount
                maxLength={255}
                placeholder="e.g., Category 1, Category 10 Science"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="category_short_code"
              label="Category Short Code"
              rules={[
                { required: true, message: 'Please enter the category short code' },
                { max: 10, message: 'Maximum 10 characters allowed' },
              ]}
            >
              <Input
                showCount
                maxLength={10}
                placeholder="e.g. AC, BT"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            {!isEditMode && <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(false)}
              disabled={isSubmitDisabled()}
            >
              Submit & Create New Category
            </Button>}
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(true)}
              disabled={isSubmitDisabled()}
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CategoryModel;
